<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { __, normalizeOptions } from '@oat-sa-private/ui-core';
    import adjustGridProps from './adjustGridProps.js';

    const dispatch = createEventDispatcher();

    export let name;
    export let value = '';
    export let options = [];
    export let disabled = false;
    export let layout = 'stacked';
    export let gridProps = {};

    const optionsNum = Object.keys(options).length;
    const { rowNum, colNum } = adjustGridProps(gridProps.rowNum, gridProps.colNum, optionsNum);

    const layouts = ['stacked', 'grid', 'row'];
    if (!layouts.includes(layout)) {
        layout = 'stacked';
    }

    let optionsArray = normalizeOptions(options);

    function handleChange() {
        dispatch('change', {
            value
        });
    }
</script>

<style>
    .radio-group {
        --radio-size: var(--fontsize-body, 2rem);
        --options-distance: var(--fontsize-body, 2rem);

        &.stacked {
            display: flex;
            justify-content: flex-start;
            flex-direction: column;
            gap: var(--options-distance);
        }

        &.grid {
            display: grid;
            grid-template-rows: repeat(var(--rowNum), auto);
            grid-template-columns: repeat(var(--colNum), auto);
            gap: var(--options-distance);
        }

        &.row {
            display: flex;
            column-gap: 3rem;
        }

        & .radio {
            & input {
                @add-mixin visually-hidden;

                &:disabled + label {
                    cursor: not-allowed;
                    & .radio-border {
                        stroke: var(--color-text-disabled);
                    }

                    & .radio-check {
                        fill: var(--color-text-disabled);
                    }

                    & .radio-label-text {
                        cursor: not-allowed;
                        color: var(--color-text-disabled);
                    }
                }

                &:not(:disabled) + label:hover .radio-border {
                    stroke: var(--color-border-actionable-hover);
                }
            }

            & label {
                display: flex;
                align-items: center;
            }

            & .radio-button {
                background-color: var(--color-bg-default);
                width: var(--radio-size);
                height: var(--radio-size);
                border-radius: 100%;
                margin-inline-end: calc(var(--fontsize-body) / 2);

                & .radio-check {
                    fill: var(--color-text-default);
                }
                & .radio-border {
                    stroke: var(--color-border-default);
                }
            }
        }
    }

    :global(.radio-group .radio input:focus-visible + label) {
        @add-mixin simple-outline;

        & .radio-border {
            stroke: var(--color-border-actionable-hover);
        }
    }

    :global-nested(.inverted) {
        & .radio-group .radio {
            & input:focus-visible {
                & + label {
                    @add-mixin simple-outline var(--color-border-focus-inverted);

                    & .radio-border {
                        stroke: var(--color-border-inverted);
                    }
                }
            }
            & label:hover {
                & .radio-border {
                    stroke: var(--color-border-inverted);
                }
            }
            & .radio-button {
                background-color: var(--color-bg-inverted);

                & .radio-check {
                    fill: var(--color-text-inverted);
                }
                & .radio-border {
                    stroke: var(--color-border-inverted);
                }
            }
        }
    }
</style>

<div class="radio-group {layout}" style="--rowNum:{rowNum}; --colNum:{colNum}">
    {#each optionsArray as option}
        <div class="radio">
            <input
                type="radio"
                id={option.id}
                {name}
                value={option.key}
                {disabled}
                bind:group={value}
                on:change={handleChange} />
            <label class="radio-label" for={option.id}>
                <svg class="radio-button" viewBox="0 0 16 16">
                    <title>{__('Radio button')}</title>
                    {#if value === option.key}
                        <circle class="radio-check" r="4" cx="8" cy="8" />
                    {/if}
                    <circle class="radio-border" r="7" cx="8" cy="8" fill="none" stroke-width="2" />
                </svg>
                <span class="radio-label-text">{option.label}</span>
            </label>
        </div>
    {/each}
</div>
